/**
 * InlineBlockMenu — Notion-style "+"-Handle + Mini-Menü.
 *
 * Verhalten (Alex 2026-05-30 v2-Feedback):
 *   - "+" erscheint sobald die Maus über IRGENDEINEM Paragraph
 *     im Editor schwebt (nicht nur am Cursor).
 *   - Click "+" → kleines Menü mit Frage / Aufgabe / Product.
 *   - Click Type → DIREKT POST /sections/ mit leerem Body, dann
 *     wird der Cursor in den frisch erzeugten Content-Paragraph
 *     der neuen Section gesetzt. Kein zweites Form-Overlay.
 *   - Annotation NICHT hier — kommt über Text-Selection.
 *
 * Styling: app-vokabular (Roboto, --accent, scharfkantig).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import {
  HelpCircle,
  CheckCircle,
  Box,
  Vote as VoteIcon,
} from 'lucide-react'
import { CONTENT_API } from '../config'
import { getAccessToken, getUser } from '../auth'

interface Props {
  editor: Editor | null
  postId: number
  onCreated?: (sectionId: string) => void
}

type SectionType = 'question' | 'task' | 'product' | 'vote'
type Mode = 'closed' | 'menu' | 'vote-form'

interface HandlePos {
  top: number
  left: number
  pmPos: number
}

export function InlineBlockMenu({ editor, postId, onCreated }: Props) {
  const [pos, setPos] = useState<HandlePos | null>(null)
  const [mode, setMode] = useState<Mode>('closed')
  const [saving, setSaving] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const stickyPosRef = useRef<HandlePos | null>(null)
  // Grace-period timer: when the mouse leaves the editor, we don't
  // hide the "+" immediately — we wait 350 ms so the user can move
  // onto the handle without it disappearing under their cursor.
  const hideTimerRef = useRef<number | null>(null)

  const cancelHide = () => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }
  const scheduleHide = () => {
    cancelHide()
    hideTimerRef.current = window.setTimeout(() => {
      if (mode === 'closed') setPos(null)
      hideTimerRef.current = null
    }, 350)
  }

  // Hover-Detection: track which paragraph the mouse is over.
  useEffect(() => {
    if (!editor) return
    const root = editor.view.dom as HTMLElement
    const onMove = (e: MouseEvent) => {
      if (mode !== 'closed') return
      cancelHide()
      const target = e.target as HTMLElement | null
      if (!target) return
      const block = target.closest('p, h1, h2, h3, h4, h5, h6') as
        | HTMLElement
        | null
      if (!block || !root.contains(block)) return
      const rect = block.getBoundingClientRect()
      try {
        const pmPos = editor.view.posAtCoords({
          left: rect.left + 1,
          top: rect.top + rect.height / 2,
        })?.pos
        if (pmPos == null) return
        setPos({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          pmPos,
        })
      } catch {
        /* ignore */
      }
    }
    const onLeave = () => {
      if (mode === 'closed') scheduleHide()
    }
    root.addEventListener('mousemove', onMove)
    root.addEventListener('mouseleave', onLeave)
    return () => {
      root.removeEventListener('mousemove', onMove)
      root.removeEventListener('mouseleave', onLeave)
      cancelHide()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, mode])

  // Outside-click + Escape close the menu.
  useEffect(() => {
    if (mode === 'closed') return
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu()
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const openMenu = () => {
    stickyPosRef.current = pos
    setMode('menu')
  }
  const closeMenu = () => {
    stickyPosRef.current = null
    setMode('closed')
  }

  const createSection = useCallback(
    async (
      type: SectionType,
      voteExtras?: { question: string; options: string; threshold: string },
    ) => {
      if (!editor) return
      const token = getAccessToken()
      if (!token) return
      const user = getUser()
      const initiator = user
        ? `${user.first_name} ${user.last_name}`.trim() || user.email
        : 'Unknown'
      const attrs: Record<string, string> = { initiator }
      if (type === 'question') attrs.status = 'open'
      else if (type === 'task') attrs.status = 'open'
      else if (type === 'product') attrs.mode = 'draft'
      else if (type === 'vote') {
        if (!voteExtras) return
        attrs.status = 'open'
        attrs.voters = 'open'
        attrs.question = voteExtras.question
        attrs.options = voteExtras.options
        attrs.threshold = voteExtras.threshold
      }

      // Compute the 0-based paragraph index of the paragraph the
      // user clicked the "+" on so the backend inserts the section
      // there instead of appending at the doc end (Alex 2026-05-30:
      // "section landet nicht da wo ich + geklickt habe").
      const targetPmPos = stickyPosRef.current?.pmPos ?? pos?.pmPos
      let paragraphIndex: number | null = null
      if (targetPmPos != null) {
        let idx = -1
        let found = -1
        editor.state.doc.descendants((node, nodePos) => {
          if (found >= 0) return false
          if (
            node.type.name === 'paragraph' ||
            node.type.name === 'heading'
          ) {
            idx++
            if (
              targetPmPos >= nodePos &&
              targetPmPos <= nodePos + node.nodeSize
            ) {
              found = idx
              return false
            }
          }
          return true
        })
        if (found >= 0) paragraphIndex = found
      }

      setSaving(true)
      try {
        const r = await fetch(
          `${CONTENT_API}/api/v1/posts/${postId}/sections/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              type,
              attrs,
              initial_text: '',
              paragraph_index: paragraphIndex,
            }),
          },
        )
        if (!r.ok) {
          const t = await r.text()
          alert(`Section konnte nicht angelegt werden: ${r.status} ${t}`)
          return
        }
        const data = (await r.json()) as { id?: string }
        closeMenu()
        if (data.id) {
          onCreated?.(data.id)
          // Wait one tick for Yjs to sync, then focus into the new
          // section's content paragraph.
          window.setTimeout(() => focusIntoSection(editor, data.id!), 150)
        }
      } catch (e) {
        alert(`Netzwerk-Fehler: ${e instanceof Error ? e.message : String(e)}`)
      } finally {
        setSaving(false)
      }
    },
    [editor, postId, onCreated],
  )

  if (!editor) return null

  const displayPos = mode === 'closed' ? pos : stickyPosRef.current
  if (!displayPos) return null

  return (
    <>
      {mode === 'closed' && (
        <button
          type="button"
          className="inline-block-handle"
          style={{
            position: 'absolute',
            top: displayPos.top + 2,
            // Clamp inside the viewport: if the paragraph's left edge
            // is already near the page-edge, place the "+" just inside
            // (at left+2) instead of off-screen. Otherwise put it in
            // the left-margin gutter (left - 26).
            left:
              displayPos.left - 26 < 4
                ? Math.max(4, displayPos.left + 2)
                : displayPos.left - 26,
          }}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          onClick={openMenu}
          title="Neue Section einfügen"
          aria-label="Neue Section einfügen"
        >
          +
        </button>
      )}
      {mode === 'menu' && (
        <div
          ref={menuRef}
          className="inline-block-menu"
          style={{
            position: 'absolute',
            top: displayPos.top + 24,
            left: Math.max(0, displayPos.left - 8),
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <MenuItem
            Icon={HelpCircle}
            label="Frage"
            hint="Reply-Thread im Doc"
            disabled={saving}
            onClick={() => createSection('question')}
          />
          <MenuItem
            Icon={CheckCircle}
            label="Aufgabe"
            hint="für jemanden"
            disabled={saving}
            onClick={() => createSection('task')}
          />
          <MenuItem
            Icon={Box}
            label="Product"
            hint="bearbeitetes Artefakt"
            disabled={saving}
            onClick={() => createSection('product')}
          />
          <MenuItem
            Icon={VoteIcon}
            label="Abstimmung"
            hint="Vote mit Optionen"
            disabled={saving}
            onClick={() => {
              stickyPosRef.current = stickyPosRef.current || pos
              setMode('vote-form')
            }}
          />
        </div>
      )}
      {mode === 'vote-form' && (
        <div
          ref={menuRef}
          className="inline-block-menu inline-block-menu--vote-form"
          style={{
            position: 'absolute',
            top: displayPos.top + 24,
            left: Math.max(0, displayPos.left - 8),
            width: 320,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <VoteForm
            saving={saving}
            onSubmit={(q, opts, threshold) =>
              createSection('vote', { question: q, options: opts, threshold })
            }
            onCancel={closeMenu}
          />
        </div>
      )}
    </>
  )
}

function VoteForm({
  saving,
  onSubmit,
  onCancel,
}: {
  saving: boolean
  onSubmit: (question: string, options: string, threshold: string) => void
  onCancel: () => void
}) {
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState('')
  const [threshold, setThreshold] = useState('majority')
  const optsList = options
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const canSubmit = question.trim() && optsList.length >= 2 && !saving
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 8 }}>
      <label style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: 0.6 }}>
        FRAGE
      </label>
      <textarea
        autoFocus
        rows={2}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Worüber wird abgestimmt?"
        disabled={saving}
        className="inline-adder-form-input"
      />
      <label style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: 0.6 }}>
        OPTIONEN (Pflicht, mindestens 2, mit Komma getrennt)
      </label>
      <input
        type="text"
        value={options}
        onChange={(e) => setOptions(e.target.value)}
        placeholder="z.B. Ja,Nein,Vielleicht"
        disabled={saving}
        className="inline-adder-form-input"
      />
      <label style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: 0.6 }}>
        SCHWELLE
      </label>
      <select
        value={threshold}
        onChange={(e) => setThreshold(e.target.value)}
        disabled={saving}
        className="inline-adder-form-input"
      >
        <option value="majority">Mehrheit (&gt;50%)</option>
        <option value="all">Einstimmig</option>
      </select>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-adder-form-cancel"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={() => onSubmit(question.trim(), optsList.join(','), threshold)}
          disabled={!canSubmit}
          className="inline-adder-form-submit"
        >
          {saving ? '…' : 'Anlegen'}
        </button>
      </div>
    </div>
  )
}

function MenuItem({
  Icon,
  label,
  hint,
  disabled,
  onClick,
}: {
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>
  label: string
  hint: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="inline-block-menu-item"
      onClick={onClick}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
    >
      <span className="inline-block-menu-item-icon">
        <Icon size={14} strokeWidth={1.75} />
      </span>
      <span className="inline-block-menu-item-text">
        <span className="inline-block-menu-item-label">{label}</span>
        <span className="inline-block-menu-item-hint">{hint}</span>
      </span>
    </button>
  )
}

/**
 * Walk the PM doc for the first paragraph whose `section_id` matches
 * the freshly-created section AND whose `section_role` is 'content'
 * (or unknown), then set the cursor there. Done after a short delay
 * so the Yjs WS update has time to propagate to the editor.
 */
function focusIntoSection(editor: Editor, sectionId: string): void {
  let attempts = 0
  const tryFocus = () => {
    attempts++
    let targetPos: number | null = null
    editor.state.doc.descendants((node, nodePos) => {
      if (targetPos !== null) return false
      if (node.type.name !== 'paragraph' && node.type.name !== 'heading') {
        return true
      }
      const sid = (node.attrs as Record<string, unknown>).section_id
      const role = (node.attrs as Record<string, unknown>).section_role
      if (sid === sectionId && (!role || role === 'content')) {
        targetPos = nodePos + 1 // inside the paragraph
        return false
      }
      return false
    })
    if (targetPos !== null) {
      editor.commands.focus(targetPos)
      // If the placeholder zero-width-space is there, drop it so the
      // user starts on a truly empty line.
      const $pos = editor.state.doc.resolve(targetPos)
      const node = $pos.parent
      if (node.textContent === '​') {
        editor.commands.command(({ tr }) => {
          tr.delete(targetPos!, targetPos! + 1)
          return true
        })
      }
      return
    }
    if (attempts < 12) window.setTimeout(tryFocus, 150)
  }
  tryFocus()
}
